
import { CdkScrollable, ScrollDispatcher } from '@angular/cdk/scrolling';
import { Directive, ElementRef, OnInit, Renderer2, signal, WritableSignal, computed, Signal, NgZone } from '@angular/core';
import { MeService } from '../../services/me.service'; // Assuming path is correct
import { UiElementComponent } from '../ui-element/ui-element'; // Assuming path is correct

@Directive() // Add selector: '[uiScrollable]' if this is meant to be used as an attribute directive
export class UiScrollableComponent extends UiElementComponent implements OnInit {
  // Scrolling
  // Ensure scrollElement is properly initialized by the derived component,
  // e.g., in its constructor or ngOnInit, pointing to the HTML element that scrolls.
  public scrollElement?: ElementRef<HTMLElement>;
  public scrollLastOffset: number = 0;

  // Refactored to Signal
  public scrolledMin: WritableSignal<boolean> = signal(false);
  public scrollState: Signal<string | undefined> = computed(() => {
    return this.scrolledMin() ? 'scrolled' : undefined;
  });


  public scrolled: boolean = false;
  public scrollDir: string | null = null; // Can be 'up', 'down', or null
  public scrollThreshold: number | undefined = undefined; // Optional threshold

  constructor(
    me: MeService,
    renderer: Renderer2,
    host: ElementRef,
    private scrollDispatcher: ScrollDispatcher,
    private ngZone: NgZone
  ) {
    super(me, renderer, host);
  }

  ngOnInit(): void {
    // It's crucial that `this.scrollElement` is defined by the time ngOnInit is called,
    // or shortly thereafter, and before scroll events are processed.
    // If `this.scrollElement` might be initialized later, the subscription logic
    // needs to account for that, or be initialized once `scrollElement` is available.

    this.scrollDispatcher
      .scrolled(100) // Throttle scroll events to every 20ms for better responsiveness
      .subscribe((cdkScrollable: CdkScrollable | void) => {
        // Ensure cdkScrollable is defined and refers to the correct scroll element
        if (
          cdkScrollable &&
          this.scrollElement && // Make sure scrollElement is set
          cdkScrollable.getElementRef().nativeElement === this.scrollElement.nativeElement
        ) {


          // Re-enter the zone to update the state.
          // ScrollDispatcher runs outside the zone for performance, but we need
          // to be inside for change detection to pick up these changes.
          this.ngZone.run(() => {

            const targetElement = cdkScrollable.getElementRef().nativeElement;
            const offset = cdkScrollable.measureScrollOffset('top');

            // Calculate threshold only if not predefined.
            // Consider calculating this once if element height is stable and threshold isn't set.
            const threshold = this.scrollThreshold ?? targetElement.offsetHeight / 2;
            const delta = 4; // A small tolerance value for comparisons

            // 1. Update scrolledMin state
            const newScrolledMin = offset > delta;
            if (this.scrolledMin() !== newScrolledMin) {
              this.scrolledMin.set(newScrolledMin);
            }

            // 2. Update scrolled state (implements hysteresis)
            // scrolled becomes true if offset goes above threshold + delta
            // scrolled becomes false if offset goes below threshold - delta
            // It retains its current value if offset is between these two marks.
            let newScrolled = this.scrolled; // Assume current state initially
            if (offset > threshold + delta) {
              newScrolled = true;
            } else if (offset < threshold - delta) {
              newScrolled = false;
            }
            if (this.scrolled !== newScrolled) {
              this.scrolled = newScrolled;
            }

            // 3. Update scrollDir state (corrected logic)
            const diff = offset - this.scrollLastOffset;
            let newScrollDir: string | null = null; // Default to null (stationary or insignificant change)

            if (diff > delta) {
              // Moved down by more than delta
              newScrollDir = 'down';
            } else if (diff < -delta) {
              // Moved up by more than delta
              newScrollDir = 'up';
            }
            // If diff is between -delta and delta (inclusive), newScrollDir remains null,
            // indicating no significant directional change.
            if (this.scrollDir !== newScrollDir) {
              this.scrollDir = newScrollDir;
            }

            // 4. Update scrollLastOffset for the next event's calculation.
            // This assignment happens on almost every throttled scroll event.
            // Since it's within zone.run(), it signals a potential change to Angular,
            // which will mark this component for check.
            this.scrollLastOffset = offset;
          });

          // Explicitly mark for check to ensure CD runs
          // this.cd.markForCheck(); // Removed - User reverted this

        }
      });
  }

  scrollToTop() {
    // this.scrollElement?.nativeElement.scrollTo({ behavior: 'smooth', top: 0 });
    this.scrollElement?.nativeElement.scrollTo(0, 0);
    this.scrollLastOffset = 0;
    this.scrolled = false;
    this.scrollDir = null;
  }

}
